import React, { useContext, useState, useEffect } from 'react';
import { ChevronLeft, AlertCircle, CheckCircle, Loader2, Building2, Calendar, CreditCard, Search, Check, Users, Plus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import subscriptionServices from '../../../../services/subscriptionServices';
import superAdminServices from '../../../../services/superAdminServices';

export default function AddSubscription() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    organization_id: '',
    organization_name: '',
    start_date: '',
    end_date: '',
    reason: '',
    max_users: 10
  });

  // Get organization from URL params if coming from organization list
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const orgId = urlParams.get('organization');
    if (orgId) {
      setFormData(prev => ({ ...prev, organization_id: orgId }));
      fetchOrganizationName(orgId);
    }
  }, [location.search]);

  const fetchOrganizationName = async (orgId) => {
    try {
      const response = await superAdminServices.getOrganizationDetails(authTokens.access, orgId);
      if (response.organization) {
        setFormData(prev => ({
          ...prev,
          organization_name: response.organization.organisation_name
        }));
      }
    } catch (err) {
      console.error('Error fetching organization name:', err);
    }
  };

  const searchOrganizations = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setOrganizations([]);
      return;
    }

    setLoadingOrganizations(true);
    try {
      const response = await superAdminServices.searchAvailableOrganizations(
        authTokens.access, 
        searchTerm, 
        10
      );

      if (response.success) {
        setOrganizations(response.data.results || []);
      } else {
        console.error('Error searching organizations:', response.error);
        setOrganizations([]);
      }
    } catch (err) {
      console.error('Error searching organizations:', err);
      setOrganizations([]);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user makes changes
    if (error) {
      setError('');
    }
  };

  const handleOrgSearchChange = (e) => {
    const value = e.target.value;
    setOrgSearchTerm(value);
    setFormData(prev => ({
      ...prev,
      organization_name: value,
      organization_id: ''
    }));

    if (value.length >= 2) {
      setShowOrgDropdown(true);
      searchOrganizations(value);
    } else {
      setShowOrgDropdown(false);
      setOrganizations([]);
    }
  };

  const selectOrganization = (org) => {
    setFormData(prev => ({
      ...prev,
      organization_id: org.id,
      organization_name: org.organisation_name
    }));
    setOrgSearchTerm(org.organisation_name);
    setShowOrgDropdown(false);
    setOrganizations([]);

    // Update URL with the selected organization ID
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.set('organization', org.id);
    window.history.replaceState({}, '', currentUrl);
  };

  const handleAddUsers = () => {
    setFormData(prev => ({
      ...prev,
      max_users: prev.max_users + 10
    }));
    if (error) setError('');
  };

  const handleRemoveUsers = () => {
    setFormData(prev => ({
      ...prev,
      max_users: Math.max(10, prev.max_users - 10)
    }));
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.organization_id) {
      setError('Veuillez sélectionner une organisation');
      return false;
    }

    if (!formData.start_date) {
      setError('La date de début est requise');
      return false;
    }

    if (!formData.end_date) {
      setError('La date de fin est requise');
      return false;
    }

    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);

    if (endDate <= startDate) {
      setError('La date de fin doit être postérieure à la date de début');
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (startDate < today) {
      setError('La date de début ne peut pas être dans le passé');
      return false;
    }

    if (formData.max_users < 10) {
      setError('Le nombre minimum d\'utilisateurs est 10');
      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const subscriptionData = {
        organization_id: parseInt(formData.organization_id),
        start_date: subscriptionServices.formatDateForAPI(formData.start_date),
        end_date: subscriptionServices.formatDateForAPI(formData.end_date),
        reason: formData.reason.trim() || 'Nouvel abonnement créé',
        max_users: formData.max_users
      };

      const response = await subscriptionServices.createSubscription(authTokens.access, subscriptionData);

      if (response.success) {
        setSuccessMessage('Abonnement créé avec succès!');
        
        // Redirect after success
        setTimeout(() => {
          navigate('/admin-panel/subscriptions');
        }, 2000);
      } else {
        throw new Error(response.error || 'Erreur lors de la création de l\'abonnement');
      }
    } catch (err) {
      setError(err.message || 'Une erreur est survenue lors de la création de l\'abonnement');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowOrgDropdown(false);
    };

    if (showOrgDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showOrgDropdown]);

  return (
    <div className="p-5 sm:p-6 max-w-4xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={handleCancel}
          className="mr-3 p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Créer un nouvel abonnement</h1>
          <p className="text-sm text-gray-400 mt-1">
            Ajoutez un abonnement pour une organisation
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <CreditCard className="h-5 w-5 text-blue-600 mr-2" />
            <h2 className="text-md font-semibold text-gray-900">Informations de l'abonnement</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Organization Selection */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organisation *
              </label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  {formData.organization_id && (
                    <Check className="absolute right-3 top-3 h-4 w-4 text-green-600" />
                  )}
                  <input
                    type="text"
                    value={formData.organization_name}
                    onChange={handleOrgSearchChange}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (formData.organization_name.length >= 2) {
                        setShowOrgDropdown(true);
                      }
                    }}
                    placeholder="Rechercher une organisation..."
                    className={`w-full pl-10 ${formData.organization_id ? 'pr-10' : 'pr-3'} py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formData.organization_id 
                        ? 'border-green-300 bg-green-50' 
                        : 'border-gray-200'
                    }`}
                    disabled={loading}
                    required
                  />
                </div>

                {/* Organization Dropdown */}
                {showOrgDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {loadingOrganizations ? (
                      <div className="p-3 text-center">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                        <span className="text-xs text-gray-500 ml-2">Recherche...</span>
                      </div>
                    ) : organizations.length > 0 ? (
                      organizations.map((org) => (
                        <button
                          key={org.id}
                          type="button"
                          onClick={() => selectOrganization(org)}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center space-x-2 border-b border-gray-100 last:border-b-0 transition-colors ${
                            formData.organization_id === org.id.toString() ? 'bg-blue-50 border-blue-200' : ''
                          }`}
                        >
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{org.organisation_name}</div>
                            <div className="text-xs text-gray-500">
                              {org.organisation_description || 'Aucune description'}
                            </div>
                          </div>
                          {formData.organization_id === org.id.toString() && (
                            <Check className="h-4 w-4 text-blue-600" />
                          )}
                        </button>
                      ))
                    ) : orgSearchTerm.length >= 2 ? (
                      <div className="p-3 text-center text-xs text-gray-500">
                        Aucune organisation disponible trouvée
                      </div>
                    ) : orgSearchTerm.length === 1 ? (
                      <div className="p-3 text-center text-xs text-gray-500">
                        Tapez au moins 2 caractères pour rechercher...
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Tapez au moins 2 caractères pour rechercher, puis <strong>cliquez sur l'organisation souhaitée</strong> dans la liste. Seules les organisations sans aucun abonnement sont affichées.
              </p>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de début *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de fin *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* User Capacity */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre d'utilisateurs *
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-gray-600">Capacité:</span>
                    <span className="text-lg font-bold text-blue-600">{formData.max_users}</span>
                    <span className="text-sm text-gray-500">utilisateurs</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {formData.max_users > 10 && (
                      <button
                        type="button"
                        onClick={handleRemoveUsers}
                        disabled={loading}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
                      >
                        -10
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleAddUsers}
                      disabled={loading}
                      className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-green-50 hover:bg-green-100 border border-blue-200 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-1"
                    >
                      <Plus className="h-3 w-3" />
                      <span>Ajouter 10</span>
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Par défaut 10 utilisateurs. Cliquez sur "Ajouter 10" pour augmenter la capacité par tranche de 10
              </p>
            </div>

            {/* Reason */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raison / Notes
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                placeholder="Raison de la création de cet abonnement (optionnel)..."
                rows="3"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {loading ? 'Création en cours...' : 'Créer l\'abonnement'}
          </button>
        </div>
      </form>
    </div>
  );
}